import { joiResolver } from '@hookform/resolvers/joi';
import {
  deserializeHtml,
  focusEditor,
  insertNode,
} from '@udecode/plate-common';
import showErrorToast from 'api/showErrorToast';
import type { ClientPeppolStatus } from 'api/v2/clients';
import { getQuoteById } from 'api/v2/quotes';
import type { BulkEmailErrorResponse, EmailData } from 'api/v2/revenues';
import {
  BulkEmailsMethods,
  generateRevenue,
  sendInvoiceEmailAndPeppol,
  sendRevenueEmail,
} from 'api/v2/revenues';
import classNames from 'classnames';
import { Alert } from 'components/atoms/Alert';
import { Button } from 'components/atoms/Button';
import {
  FieldConnector,
  FieldErrorUI,
} from 'components/atoms/Form/FieldConnector';
import { Toggle } from 'components/atoms/Form/Toggle';
import { Loader } from 'components/atoms/Loader';
import { Menu } from 'components/atoms/Menu';
import Tooltip, {
  renderTooltip,
} from 'components/atoms/Tooltip/Tooltip.component';
import UseState from 'components/atoms/UseState';
import CustomLoader from 'components/molecules/CustomLoader.component';
import PeppolHelper from 'components/molecules/PeppolHelper.component';
import SimpleError from 'components/molecules/SimpleError.component';
import dayjs from 'dayjs';
import useGetCurrentCustomerData from 'hooks/Authentication/useGetCurrentCustomerData';
import useGetLoggedInUserData from 'hooks/Authentication/useGetLoggedInUserData';
import useCheckClientPeppolStatus from 'hooks/Peppol/useCheckClientPeppolStatus';
import useUserPeppolIntegration from 'hooks/Peppol/useUserPeppolIntegration';
import useFeatures from 'hooks/useFeatures';
import Joi from 'joi';
import uniq from 'lodash/uniq';
import { motion } from 'motion/react';
import type { FC, PropsWithChildren } from 'react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';
import { AlertIdsEnum } from 'types/AlertIdsEnum';
import type { IClient } from 'types/clients.types';
import { ClientTypeEnum } from 'types/clients.types';
import type { Currency } from 'types/global.types';
import type { PeppolCurrentIntegration } from 'types/integrations';
import { IntegrationsStatusEnum } from 'types/integrations';
import type { InvoiceEmailType } from 'types/invoice.types';
import {
  AvailableCountries,
  AvailableLanguages,
  type IUser,
} from 'types/users.types';
import {
  isAlertDismissed,
  markAlertAsDismissed,
} from 'utils/AlertsStoreManager';
import { getTracingEvents } from 'utils/amplitude';
import downloadFileFromPath from 'utils/downloadFileFromPath';
import formatMoney from 'utils/formatMoney';
import {
  CheckIconOutline,
  PlusIconOuline,
  QuestionMarkCircleIcon,
  XIconOutline,
} from 'utils/icons';
import {
  AttachmentIcon,
  FloppyDiskIcon,
  InfoIconOutline,
  XMLIcon,
} from 'utils/icons/figmaIcons';
import { divideBy1000 } from 'utils/math';
import { SentryIgnoredError } from 'utils/SentryIgnoredError';
import { FIVE_MINUTES } from 'utils/time';
import showToast from 'utils/toast';
import type { EditorRef, TextEditorType } from '../TextEditor';
import { serializeHtml } from '../TextEditor';
import {
  adaptLegacyVariables,
  convertVariablesToHtml,
  stringifyNodes,
} from '../TextEditor/serializeHtml';
import Attachment from './Attachment';
import CommonEmailFields from './CommonEmailFields.component';
import { GenericFileIcon, PDFIcon } from './CustomIcons';
import {
  PeppolClientSendingWarnings,
  PeppolUserSendingWarnings,
} from './PeppolUserAndClientSendingWarnings';
import * as amplEvents from './sendEmailAmplitude';
import SendMeTestEmail from './SendMeTestEmail';
import {
  creditNoteSubjectTranslations,
  invoiceBodyTranslations,
  invoiceSubjectTranslations,
} from './translations';
import useExternalFilesObject from './useExternalFilesObject';
import type { FileState } from './utils';
import {
  adaptAttachmentsArray,
  convertLineBreaksToP,
  convertTextNodesToElements,
  externalAttachmentsObjectToArray,
} from './utils';
import { revenueEmailValidationSchema } from './validations';

const eventsDataFn = (tracingData?: Record<string, string>) => ({
  ...tracingData,
});

const events = getTracingEvents({
  onAddAttachment: {
    name: amplEvents.WEBAPP_SENDEMAIL_ADDATTACHMENT,
    data: eventsDataFn,
  },
  onRemoveAttachment: {
    name: amplEvents.WEBAPP_SENDEMAIL_REMOVEATTACHMENT,
    data: eventsDataFn,
  },
  onToggleSaveTempOn: {
    name: amplEvents.WEBAPP_SENDEMAIL_TOGGLESAVEASTEMPLETEON,
    data: eventsDataFn,
  },
  onToggleSaveTempOff: {
    name: amplEvents.WEBAPP_SENDEMAIL_TOGGLESAVEASTEMPLETEOFF,
    data: eventsDataFn,
  },
  onToggleTrackingOn: {
    name: amplEvents.WEBAPP_SENDEMAIL_TOGGLETRACKEMAILON,
    data: eventsDataFn,
  },
  onToggleTrackingOff: {
    name: amplEvents.WEBAPP_SENDEMAIL_TOGGLETRACKEMAILOFF,
    data: eventsDataFn,
  },
  onAddVariable: {
    name: amplEvents.WEBAPP_SENDEMAIL_ADDVARIABLE,
    data: eventsDataFn,
  },
});

type EmailClientProps = {
  onSubmit: (values: InvoiceEmailType) => any;
  onTest?: (
    values: Omit<InvoiceEmailType, 'cc' | 'bcc' | 'saveTemplate'>,
  ) => any;
  tracingData?: Record<string, string>;
  hideQuoteAttachmentOption?: boolean;
  hideSaveTemplateOption?: boolean;
  hideTrackingOption?: boolean;
  hideExternalAttachmentsOption?: boolean;
  displayVariablesAsLabelsOnly?: boolean;
  staticAttachments?: boolean;
  defaultValues?: Partial<InvoiceEmailType>;
  title?: string;
  okButtonText?: string;
  context?: {
    isCreditNote?: boolean;
    defaultTemplateLang?: AvailableLanguages;
    parentQuoteId?: string;
    revenueId?: string;
    revenuePDFPath?: string;
    revenueNumber?: string;
    revenueDueDate?: string;
    clientEmail?: string;
    clientName?: string;
    revenueTotalAmountInclVAT?: number;
    revenueCurrency?: Currency;
  };
};

enum AttachmentsEnum {
  attachInvoicePdf = 'attachInvoicePdf',
  attachInvoiceUbl21 = 'attachInvoiceUbl21',
  attachInvoiceUblBe = 'attachInvoiceUblBe',
  attachInvoiceUbl31 = 'attachInvoiceUbl31',
  attachInvoiceZugferd = 'attachInvoiceZugferd',
  attachQuotePdf = 'attachQuotePdf',
}

const tempTogglesApi = {
  getSavedSendViaPeppolOption: () => {
    return localStorage.getItem('invoice.sendViaPeppol') !== '0';
  },

  getSavedSendViaEmailOption: () => {
    return localStorage.getItem('invoice.sendViaEmail') !== '0';
  },

  saveSendViaPeppolOption: (value: boolean) => {
    localStorage.setItem('invoice.sendViaPeppol', value ? '1' : '0');
  },

  saveSendViaEmailOption: (value: boolean) => {
    localStorage.setItem('invoice.sendViaEmail', value ? '1' : '0');
  },
};

const combineAttachments = (
  external: Record<string, FileState>,
  internal: string[],
): (string | { filePath: string; name: string })[] => {
  return [...externalAttachmentsObjectToArray(external), ...internal];
};

const useRevenueEmailIntialValues = (
  props: Pick<EmailClientProps, 'context' | 'defaultValues'>,
) => {
  const { context: propsContext, defaultValues: propsDefaultValues } = props;

  const userQuery = useGetCurrentCustomerData();
  const user = userQuery.data as IUser;
  const country = user.country;
  const canAttachZugferd = useFeatures('revenue.showZugferdOption') as boolean;

  const context = useRef(propsContext).current;

  const invoiceLang = context?.defaultTemplateLang || AvailableLanguages.en;

  const isCreditNote = context?.isCreditNote;

  const tContextForSubject = {
    name: user.companyName || user.firstName + ' ' + user.lastName,
  };

  const defaultTracking =
    user.settings?.invoices?.enableTracking ??
    user.country === AvailableCountries.belgium;

  const propsDefaultValuesRef = useRef(propsDefaultValues);

  const defaultSubject = useRef(
    propsDefaultValuesRef.current?.subject ??
      (isCreditNote
        ? creditNoteSubjectTranslations[country][invoiceLang](
            tContextForSubject,
          )
        : invoiceSubjectTranslations[invoiceLang](tContextForSubject)),
  );

  const defaultHTMLTemplate = useMemo(() => {
    const propsTemp = propsDefaultValuesRef.current?.htmlTemplate;
    const newTemp = user.settings.invoices?.emailHtmlTemplate;
    const legacyTemp = adaptLegacyVariables(
      convertLineBreaksToP(
        user.settings.invoices?.messageTemplate?.default?.default,
      ),
    );
    const defaultTemp = convertLineBreaksToP(
      invoiceBodyTranslations[invoiceLang](),
    );
    return propsTemp || newTemp || legacyTemp || defaultTemp || '';
  }, [user, invoiceLang]);

  const innerDefaultValues = useMemo(() => {
    const {
      to,
      cc,
      bcc,
      subject,
      htmlTemplate,
      saveTemplate,
      enableTracking,
      externalAttachments,
      ...attachments
    } = propsDefaultValuesRef.current || {};
    const defaultAttachments = Object.values(AttachmentsEnum).filter((key) => {
      return !!(attachments as any)[key];
    });

    const externalAttachmentsObject = (externalAttachments || []).reduce(
      (acc, data, i) => {
        const key = '_default_' + i;
        acc[key] = {
          key,
          path: data.filePath,
          name: data.name,
          type: data.filePath.split('.').pop() as string,
        };
        return acc;
      },
      {} as Record<string, Omit<FileState, 'cancel' | 'file'>>,
    );

    return {
      to: to || (context?.clientEmail ? [context.clientEmail] : []),
      cc: cc || [user.email],
      bcc: bcc || [],
      subject: defaultSubject.current,
      htmlTemplate: defaultHTMLTemplate,
      saveTemplate: saveTemplate ?? false,
      enableTracking: enableTracking ?? defaultTracking,
      allAttachments: [] as (string | { filePath: string; name: string })[],
      externalAttachmentsObject,
      attachments: defaultAttachments.length
        ? defaultAttachments
        : [AttachmentsEnum.attachInvoicePdf].concat(
            canAttachZugferd ? [AttachmentsEnum.attachInvoiceZugferd] : [],
          ),
      to_draft: '',
      cc_draft: '',
      bcc_draft: '',
      sendViaPeppol: tempTogglesApi.getSavedSendViaPeppolOption(),
      sendViaEmail: tempTogglesApi.getSavedSendViaEmailOption(),
    };
  }, []);

  return innerDefaultValues;
};

type EmailClientPropsControlled = Omit<
  EmailClientProps,
  'onSubmit' | 'onTest' | 'okButtonText' | 'title'
>;

const RevenueEmailClientControlled: FC<EmailClientPropsControlled> = ({
  tracingData,
  context: propsContext,
  hideQuoteAttachmentOption,
  hideSaveTemplateOption,
  hideTrackingOption,
  displayVariablesAsLabelsOnly,
  hideExternalAttachmentsOption,
  staticAttachments,
}) => {
  const { t } = useTranslation();
  const showUBLOptions = useFeatures('revenue.showUBLOptions') as boolean;
  const showUBL31Option = useFeatures('revenue.showUBL31Option') as boolean;
  const showZugferdOption = useFeatures('revenue.showZugferdOption') as boolean;
  const userQuery = useGetCurrentCustomerData();
  const user = userQuery.data as IUser;
  const country = user.country;

  const context = useRef(propsContext).current;

  const dateFormat: string = useFeatures('settings.dateFormat');

  const isCreditNote = context?.isCreditNote;

  const variablesList = useMemo(() => {
    return [
      {
        key: 'revenueNumber',
        value: displayVariablesAsLabelsOnly ? null : context?.revenueNumber,
        label: t(
          isCreditNote
            ? 'email_client.variables.credit_note_number.' + country
            : 'email_client.variables.invoice_number',
        ),
      },
      {
        key: 'client.name',
        value: displayVariablesAsLabelsOnly ? null : context?.clientName,
        label: t('email_client.variables.client_name'),
      },
      {
        key: 'totalAmountInclVAT',
        value:
          displayVariablesAsLabelsOnly ||
          context?.revenueTotalAmountInclVAT == null
            ? null
            : formatMoney(
                divideBy1000(context.revenueTotalAmountInclVAT),
                context?.revenueCurrency,
              ),
        label: t(
          isCreditNote
            ? 'email_client.variables.credit_note_amount.' + country
            : 'email_client.variables.invoice_amount',
        ),
      },
      {
        key: 'dueDate',
        value:
          displayVariablesAsLabelsOnly || context?.revenueDueDate == null
            ? null
            : dayjs(context?.revenueDueDate).format(dateFormat),
        label: t(
          isCreditNote
            ? 'email_client.variables.credit_note_due_date.' + country
            : 'email_client.variables.invoice_due_date',
        ),
      },
      {
        key: 'fullname',
        value: displayVariablesAsLabelsOnly
          ? null
          : user.firstName + ' ' + user.lastName,
        label: t('email_client.variables.fullname'),
      },
    ].map((variable) => ({
      ...variable,
      tooltip: displayVariablesAsLabelsOnly
        ? t('email_client.variables.lazy_variable.tooltip')
        : null,
    }));
  }, [context, user, isCreditNote, displayVariablesAsLabelsOnly]);

  const formAPI =
    useFormContext<ReturnType<typeof useRevenueEmailIntialValues>>();

  const errors = formAPI.formState.errors;

  const editorRef: EditorRef = useRef();

  const variablesData = useMemo(() => {
    return variablesList.reduce(
      (acc, variable) => ({
        ...acc,
        [variable.key]: {
          ...variable,
          value: variable.value ?? variable.label,
        },
      }),
      {},
    );
  }, [variablesList]);

  useEffect(() => {
    if (!editorRef.current) return;

    const nodes = deserializeHtml(editorRef.current, {
      element: convertVariablesToHtml(
        formAPI.getValues().htmlTemplate,
        variablesData,
      ),
    });
    editorRef.current.children = convertTextNodesToElements(nodes);
  }, []);

  const subjectRef: EditorRef = useRef();

  useEffect(() => {
    if (!subjectRef.current) return;
    const nodes = deserializeHtml(subjectRef.current, {
      element: convertVariablesToHtml(
        formAPI.getValues().subject,
        variablesData,
      ),
    });
    subjectRef.current.children = convertTextNodesToElements(nodes);
  }, []);

  // fixed possible attachments
  const revenueTypeAndNumber = `${
    isCreditNote
      ? t(`invoices.filters.${country}.credit_note`)
      : t('invoice.types.invoice')
  }${context?.revenueNumber ? ` ${context.revenueNumber}` : ''}`;
  const PDFName = `${revenueTypeAndNumber}.pdf`;
  const UBL21Name = 'UBL 2.1.xml';
  const UBLBeName = 'UBL EN 16931-1:2017.xml';
  const UBL31Name = t('email_client.xrechnung_file_name');
  const ZugferdName = `${revenueTypeAndNumber} ZUGFeRD.pdf`;
  const QuoteName = t('quote') + '.pdf';

  const [attachments, saveTemplate, enableTracking] = formAPI.watch([
    'attachments',
    'saveTemplate',
    'enableTracking',
  ]);

  const trackingToggleId = useId();
  const trackingtooltipId = useId();

  const {
    attachInvoicePdf,
    attachInvoiceUbl21,
    attachInvoiceUblBe,
    attachInvoiceUbl31,
    attachInvoiceZugferd,
    attachQuotePdf,
  } = adaptAttachmentsArray(attachments);

  const [showTrackingTooltipInitially] = useState(
    () =>
      !isAlertDismissed(AlertIdsEnum.emailTrackingTooltip) &&
      formAPI.getValues().enableTracking,
  );

  const [showZugferdTooltip, setShowZugferdTooltip] = useState(
    () =>
      !isAlertDismissed(AlertIdsEnum.emailZugferdTooltip) &&
      attachInvoiceZugferd,
  );

  const removeAttachment = (key: string) => {
    events.onRemoveAttachment({ ...tracingData, attachment: key });
    formAPI.setValue(
      'attachments',
      attachments.filter((a) => a !== key),
    );
  };

  const addAttachment = (key: AttachmentsEnum) => {
    events.onAddAttachment({ ...tracingData, attachment: key });
    formAPI.setValue('attachments', uniq(attachments.concat(key)), {
      shouldValidate: true,
    });
  };

  const renderVariables = (editor: TextEditorType) => {
    return variablesList.map((variable) => (
      <Menu.Button
        key={variable.key}
        onClick={() => {
          events.onAddVariable(tracingData);
          insertNode(editor, {
            ...variable,
            type: 'variable',
            value: variable.value ?? variable.label,
            variable: variable.key,
            children: [{ text: '' }],
          });
          focusEditor(editor);
          editor.move();
        }}
      >
        {variable.label}
      </Menu.Button>
    ));
  };

  const { uploaderAPI, filesAPI, hasOpenUploadSlots, processFile } =
    useExternalFilesObject({
      getFieldValue: () => formAPI.getValues().externalAttachmentsObject,
      setFieldValue: (value) => {
        formAPI.setValue('externalAttachmentsObject', value);
      },
    });

  return (
    <>
      {!hideExternalAttachmentsOption && (
        <input {...uploaderAPI.getInputProps()} />
      )}
      <CommonEmailFields
        editorRef={editorRef}
        subjectRef={subjectRef}
        tracingData={tracingData}
        variablesData={variablesData}
        editorOnChange={() => {
          formAPI.setValue(
            'htmlTemplate',
            serializeHtml(editorRef.current, editorRef.current?.children),
          );
        }}
        subjectOnChange={() => {
          formAPI.setValue(
            'subject',
            stringifyNodes(subjectRef.current?.children),
          );
        }}
        renderSubjectControls={() => {
          const editor = subjectRef.current as TextEditorType;
          return (
            <Menu
              position="right"
              rootClassName="flex"
              contentWrapperClassName="mt-5"
              trigger={() => (
                <div className="text-primary-400 text-sm font-normal hover:underline focus:underline flex items-center gap-2">
                  <PlusIconOuline className="w-3.5 h-3.5" strokeWidth="2" />
                  <span>{t('email_client.variables.add_variable')}</span>
                </div>
              )}
            >
              <Menu.Group>{renderVariables(editor)}</Menu.Group>
            </Menu>
          );
        }}
        renderAttachments={() => {
          return (
            <div className="flex flex-col gap-2 mb-4 mx-4">
              {filesAPI.values.map((fileState, i) => {
                const file = fileState.file;
                const path = fileState.path;
                const canRetry =
                  hasOpenUploadSlots && !!file && !!fileState.canRetry;
                return (
                  <Attachment
                    key={'user_files_' + i}
                    icon={<GenericFileIcon className="w-6 h-6 text-primary" />}
                    label={fileState.name}
                    isUploading={fileState.isLoading}
                    uploadingProgress={fileState.progress}
                    isError={fileState.isError}
                    retry={
                      canRetry ? () => processFile(file, fileState.key) : null
                    }
                    onClick={path ? () => downloadFileFromPath(path) : null}
                    onRemove={() => {
                      fileState.cancel?.();
                      filesAPI.remove(fileState.key);
                    }}
                  />
                );
              })}
              {attachInvoicePdf && (
                <Attachment
                  icon={<PDFIcon className="w-6 h-6 text-[#C74343]" />}
                  label={PDFName}
                  disabled={staticAttachments}
                  onRemove={() =>
                    removeAttachment(AttachmentsEnum.attachInvoicePdf)
                  }
                  onClick={
                    context?.revenuePDFPath
                      ? () =>
                          downloadFileFromPath(context.revenuePDFPath as string)
                      : null
                  }
                />
              )}
              {attachInvoiceUbl21 && (
                <Attachment
                  icon={<XMLIcon className="w-6 h-6 text-[#425cc7]" />}
                  label={UBL21Name}
                  disabled={staticAttachments}
                  onRemove={() =>
                    removeAttachment(AttachmentsEnum.attachInvoiceUbl21)
                  }
                  onClick={
                    context?.revenueId
                      ? () =>
                          generateRevenue(context.revenueId as string, {
                            generateUbl21: true,
                          }).then(({ filePath }) =>
                            downloadFileFromPath(filePath),
                          )
                      : null
                  }
                />
              )}
              {attachInvoiceUblBe && (
                <Attachment
                  icon={<XMLIcon className="w-6 h-6 text-[#425cc7]" />}
                  label={UBLBeName}
                  disabled={staticAttachments}
                  onRemove={() =>
                    removeAttachment(AttachmentsEnum.attachInvoiceUblBe)
                  }
                  onClick={
                    context?.revenueId
                      ? () =>
                          generateRevenue(context?.revenueId as string, {
                            generateUblBe: true,
                          }).then(({ filePath }) =>
                            downloadFileFromPath(filePath),
                          )
                      : null
                  }
                />
              )}
              {attachInvoiceZugferd && (
                <Attachment
                  className="ZUGFERD_ATTACHMENT"
                  icon={<PDFIcon className="w-6 h-6 text-[#C74343]" />}
                  label={ZugferdName}
                  disabled={staticAttachments}
                  onRemove={() => {
                    removeAttachment(AttachmentsEnum.attachInvoiceZugferd);
                    markAlertAsDismissed(AlertIdsEnum.emailZugferdTooltip);
                    setShowZugferdTooltip(false);
                  }}
                  onClick={
                    context?.revenueId
                      ? () =>
                          generateRevenue(context?.revenueId as string, {
                            generateZugferd: true,
                          }).then(({ filePath }) =>
                            downloadFileFromPath(filePath),
                          )
                      : null
                  }
                />
              )}
              {showZugferdTooltip &&
                attachInvoiceZugferd &&
                renderTooltip({
                  anchorSelect: '.ZUGFERD_ATTACHMENT',
                  clickable: true,
                  classNameArrow: 'bg-white',
                  isOpen: true,
                  place: 'left',
                  render: () => (
                    <motion.div className="flex max-w-[280px] p-3 bg-white shadow-lg rounded-lg">
                      <InfoIconOutline className="w-5 h-5 text-primary flex-shrink-0" />
                      <div className="ml-2 mr-4">
                        <p className="mb-1 text-sm font-semibold text-primary leading-tight">
                          {t('email_client.toast.field.zugferd.tooltip.title')}
                        </p>
                        <p className="text-xs text-primary-600">
                          {t('email_client.toast.field.zugferd.tooltip.text')}
                        </p>
                      </div>
                      <Button
                        asDefault
                        className="flex-shrink-0 self-start text-primary-400 hover:text-primary-700 focus:text-primary-700"
                        onClick={() => {
                          setShowZugferdTooltip(false);
                          markAlertAsDismissed(
                            AlertIdsEnum.emailZugferdTooltip,
                          );
                        }}
                      >
                        <XIconOutline className="w-5 h-5" />
                      </Button>
                    </motion.div>
                  ),
                  overrideClassName: '!bg-transparent !p-0 !opacity-100',
                })}
              {attachInvoiceUbl31 && (
                <Attachment
                  icon={<XMLIcon className="w-6 h-6 text-[#425cc7]" />}
                  label={UBL31Name}
                  disabled={staticAttachments}
                  onRemove={() =>
                    removeAttachment(AttachmentsEnum.attachInvoiceUbl31)
                  }
                  onClick={
                    context?.revenueId
                      ? () =>
                          generateRevenue(context?.revenueId as string, {
                            generateUbl31: true,
                          }).then(({ filePath }) =>
                            downloadFileFromPath(filePath),
                          )
                      : null
                  }
                />
              )}
              {attachQuotePdf && (
                <Attachment
                  icon={<PDFIcon className="w-6 h-6 text-[#C74343]" />}
                  label={QuoteName}
                  disabled={staticAttachments}
                  onRemove={() =>
                    removeAttachment(AttachmentsEnum.attachQuotePdf)
                  }
                  onClick={
                    context?.parentQuoteId
                      ? () =>
                          getQuoteById(context.parentQuoteId as string).then(
                            (quote) => downloadFileFromPath(quote.filePath),
                          )
                      : null
                  }
                />
              )}
            </div>
          );
        }}
        renderExtraEditorControls={(editor) => {
          const menuItems = [
            !attachInvoicePdf && (
              <Menu.Button
                onClick={() => addAttachment(AttachmentsEnum.attachInvoicePdf)}
              >
                {t('email_client.options.add_pdf')}
              </Menu.Button>
            ),
            showUBLOptions && !attachInvoiceUbl21 && (
              <Menu.Button
                onClick={() =>
                  addAttachment(AttachmentsEnum.attachInvoiceUbl21)
                }
              >
                {t('email_client.options.add_ubl_21')}
              </Menu.Button>
            ),
            showUBLOptions && !attachInvoiceUblBe && (
              <Menu.Button
                onClick={() =>
                  addAttachment(AttachmentsEnum.attachInvoiceUblBe)
                }
              >
                {t('email_client.options.add_ubl_be')}
              </Menu.Button>
            ),
            showZugferdOption && !attachInvoiceZugferd && (
              <Menu.Button
                onClick={() =>
                  addAttachment(AttachmentsEnum.attachInvoiceZugferd)
                }
              >
                {t('email_client.options.add_zugferd')}
              </Menu.Button>
            ),
            showUBL31Option && !attachInvoiceUbl31 && (
              <Menu.Button
                onClick={() =>
                  addAttachment(AttachmentsEnum.attachInvoiceUbl31)
                }
              >
                {t('email_client.options.add_ubl_31')}
              </Menu.Button>
            ),
            !hideQuoteAttachmentOption && !attachQuotePdf && (
              <Menu.Button
                onClick={() => addAttachment(AttachmentsEnum.attachQuotePdf)}
              >
                {t('email_client.options.add_quote')}
              </Menu.Button>
            ),
            !hideExternalAttachmentsOption && hasOpenUploadSlots && (
              <Menu.Button
                tracingEvent={amplEvents.WEBAPP_SENDEMAIL_CLICKEDATTACHFILE}
                onClick={() => {
                  uploaderAPI.open();
                }}
              >
                <div className="leading-tight">
                  <span className="block">
                    {t('email_client.options.add_from_pc.title')}
                  </span>
                  <span className="text-sm text-primary-400">
                    {t('email_client.options.add_from_pc.desc')}
                  </span>
                </div>
              </Menu.Button>
            ),
          ].filter(Boolean);
          return (
            <>
              {(() => {
                if (!menuItems.length) return null;
                return (
                  <Menu
                    position="top"
                    rootClassName="flex"
                    contentWrapperClassName="mb-6"
                    trigger={(api) => (
                      <Tooltip
                        hoverable={false}
                        content={
                          api.open
                            ? null
                            : t('email_client.attachments.tooltip')
                        }
                      >
                        <AttachmentIcon className="text-primary-700 w-6 h-6" />
                      </Tooltip>
                    )}
                  >
                    <Menu.Group>{menuItems}</Menu.Group>
                  </Menu>
                );
              })()}
              {!hideSaveTemplateOption && (
                <Menu
                  position="top"
                  rootClassName="flex"
                  contentWrapperClassName="mb-6"
                  trigger={(api) => (
                    <Tooltip
                      hoverable={false}
                      content={
                        api.open
                          ? null
                          : t('email_client.save_template.tooltip')
                      }
                    >
                      <FloppyDiskIcon className="text-primary-700 w-6 h-6" />
                    </Tooltip>
                  )}
                >
                  <Menu.Group>
                    <Menu.Button
                      onClick={() => {
                        if (saveTemplate)
                          events.onToggleSaveTempOff(tracingData);
                        else events.onToggleSaveTempOn(tracingData);

                        formAPI.setValue('saveTemplate', !saveTemplate);
                      }}
                    >
                      <span className="flex items-center gap-2">
                        {saveTemplate && (
                          <CheckIconOutline className="w-5 h-5" />
                        )}
                        {t('email_client.options.save_as_template')}
                      </span>
                    </Menu.Button>
                  </Menu.Group>
                </Menu>
              )}
              {(menuItems.length > 0 || !hideSaveTemplateOption) && (
                <div className="h-4 border-l border-primary-200" />
              )}
              <Menu
                position="top"
                rootClassName="flex"
                contentWrapperClassName="mb-5"
                trigger={() => (
                  <div className="text-primary-700 flex items-center gap-2">
                    <PlusIconOuline className="w-3.5 h-3.5" strokeWidth="2" />
                    <span className="text-sm">
                      {t('email_client.variables.add_variable')}
                    </span>
                  </div>
                )}
              >
                <Menu.Group>{renderVariables(editor)}</Menu.Group>
              </Menu>
              <div className="ml-auto flex gap-4 items-center">
                {!hideTrackingOption && (
                  <UseState
                    init={{
                      isOpen: showTrackingTooltipInitially,
                      animate: showTrackingTooltipInitially,
                    }}
                    render={({ isOpen, animate }, setTooltipState) => (
                      <>
                        <div
                          id={trackingtooltipId}
                          className="flex items-center gap-2"
                        >
                          <label
                            htmlFor={trackingToggleId}
                            className="text-sm text-primary-700 flex items-center gap-1"
                          >
                            {t('email_client.toast.field.tracking.label')}
                            <QuestionMarkCircleIcon className="w-4.5 h-4.5 text-primary-200" />
                          </label>
                          <Toggle
                            id={trackingToggleId}
                            enabled={enableTracking}
                            onChange={(bool) => {
                              formAPI.setValue('enableTracking', bool);
                            }}
                          />
                        </div>
                        {renderTooltip({
                          anchorId: trackingtooltipId,
                          clickable: true,
                          noArrow: true,
                          render: () => (
                            <motion.div
                              {...(animate
                                ? {
                                    initial: { opacity: 0 },
                                    animate: {
                                      opacity: 1,
                                      transition: { delay: 0.5 },
                                    },
                                  }
                                : null)}
                              className="text-center max-w-[250px] text-sm font-semibold p-2 bg-white shadow-lg text-primary-700 rounded-lg"
                              onHoverStart={() => {
                                markAlertAsDismissed(
                                  AlertIdsEnum.emailTrackingTooltip,
                                );
                                setTooltipState({ isOpen: false });
                              }}
                            >
                              {t('email_client.toast.field.tracking.tooltip')}
                            </motion.div>
                          ),
                          overrideClassName:
                            '!bg-transparent !p-0 !opacity-100',
                          isOpen,
                          setIsOpen: (bool) => {
                            if (!bool)
                              markAlertAsDismissed(
                                AlertIdsEnum.emailTrackingTooltip,
                              );
                            setTooltipState({ isOpen: bool });
                          },
                        })}
                      </>
                    )}
                  />
                )}
              </div>
            </>
          );
        }}
      />

      {!!errors.allAttachments && (
        <FieldErrorUI className="mt-1">
          {t('email_client.fields.one_or_more_attachments')}
        </FieldErrorUI>
      )}
    </>
  );
};

const LayoutWithTitle: FC<
  PropsWithChildren<{ isCreditNote?: boolean; titleClassName?: string }>
> = ({ isCreditNote, titleClassName, children }) => {
  const { t } = useTranslation();
  const user = useGetCurrentCustomerData().data;
  return (
    <div className="w-full bg-white p-2">
      <h1
        className={classNames(
          'text-3xl leading-tight tracking-tight font-avenir font-bold text-primary-700',
          titleClassName,
        )}
      >
        {isCreditNote
          ? t('email_client.credit_note.title.' + user?.country)
          : t('email_client.invoice.title')}
      </h1>
      {children}
    </div>
  );
};

export const RevenueEmailClientBasic: FC<EmailClientProps> = (props) => {
  const { t } = useTranslation();
  const { data: user } = useGetCurrentCustomerData();
  const userOrExpertEmail = useGetLoggedInUserData().data?.email as string;

  const intials = useRevenueEmailIntialValues({
    context: props.context,
    defaultValues: props.defaultValues,
  });

  const formAPI = useForm({
    defaultValues: intials,
    resolver: (values, context, ...rest) => {
      const allAttachments = combineAttachments(
        values.externalAttachmentsObject,
        values.attachments,
      );
      return joiResolver(revenueEmailValidationSchema, {
        abortEarly: false,
        allowUnknown: true,
      })({ ...values, allAttachments }, context, ...rest);
    },
  });

  console.log(formAPI.formState.errors);

  const externalAttachmentsObject = formAPI.watch('externalAttachmentsObject');
  const isUploadingExternalAttachments = Object.values(
    externalAttachmentsObject,
  ).some((fileState) => fileState.isLoading);

  const isCreditNote = props.context?.isCreditNote;

  const { isSubmitting } = formAPI.formState;

  const isSubmitDisabled = isSubmitting || isUploadingExternalAttachments;

  return (
    <FormProvider {...formAPI}>
      <LayoutWithTitle
        isCreditNote={props.context?.isCreditNote}
        titleClassName="mb-6"
      >
        <RevenueEmailClientControlled {...props} />
        <div className="flex items-center justify-end gap-4 mt-4">
          {props.onTest && (
            <SendMeTestEmail
              disabled={isSubmitDisabled}
              tracingEvent={{
                name: amplEvents.WEBAPP_SENDEMAIL_CLICKEDSENDMETESTEMAIL,
                data: props.tracingData,
              }}
              onClick={() => {
                return formAPI
                  .trigger(['subject', 'htmlTemplate', 'attachments'])
                  .then((isValid) => {
                    if (!isValid) return Promise.reject();
                    const {
                      subject,
                      htmlTemplate,
                      attachments,
                      externalAttachmentsObject,
                    } = formAPI.getValues();
                    const data = {
                      to: [userOrExpertEmail],
                      subject,
                      htmlTemplate,
                      saveTemplate: false,
                      enableTracking: false,
                      ...adaptAttachmentsArray(attachments),
                      externalAttachments: externalAttachmentsObjectToArray(
                        externalAttachmentsObject,
                      ),
                    };
                    return props.onTest?.(data);
                  });
              }}
            />
          )}
          <Button
            disabled={isSubmitDisabled}
            tracingEvent={{
              name: amplEvents.WEBAPP_SENDEMAIL_CLICKEDSUBMITEMAIL,
              data: { ...props.tracingData, method: 'email' },
            }}
            onClick={() => {
              formAPI.handleSubmit((values) => {
                const data = {
                  ...values,
                  // add all attacments options as false, the override what we need to allow,
                  // keep it that way it can be useful in case of no attachments are required
                  // so we can diffrentiate between the empty states for example is it empty by us or by the user ( {} vs {attachment1: false} )
                  ...Object.values(AttachmentsEnum).reduce(
                    (acc, key) => ({ ...acc, [key]: false }),
                    {},
                  ),
                  // override the wanted attachments
                  ...adaptAttachmentsArray(values.attachments),
                  attachments: undefined,
                  externalAttachmentsObject: undefined,
                  allAttachments: undefined,
                  externalAttachments: externalAttachmentsObjectToArray(
                    values.externalAttachmentsObject,
                  ),
                };
                return props.onSubmit(data);
              })();
            }}
          >
            <span className="flex items-center gap-2">
              {isSubmitting && <Loader size="lg" />}
              {props.okButtonText ??
                (isCreditNote
                  ? t('email_client.credit_note.submit.' + user?.country)
                  : t('email_client.invoice.submit'))}
            </span>
          </Button>
        </div>
      </LayoutWithTitle>
    </FormProvider>
  );
};

export const SendInvoiceClient: FC<
  Omit<EmailClientProps, 'onSubmit' | 'onTest'> & {
    onAfterSend?: () => any;
    context: EmailClientProps['context'] & { revenueId: string };
  }
> = (props) => {
  const { t } = useTranslation();
  const { refetch: refetchUser } = useGetCurrentCustomerData();

  const onSavingTemplate = () => {
    refetchUser();
    showToast(
      {
        title: t('email_client.toast.template_saved'),
      },
      { type: 'success' },
    );
  };

  const onAfterSend = () => {
    return Promise.resolve().then(props.onAfterSend).catch(console.error);
  };

  return (
    <RevenueEmailClientBasic
      {...props}
      onTest={(values) => sendRevenueEmail(props.context.revenueId, values)}
      onSubmit={(values) =>
        sendRevenueEmail(props.context.revenueId, values).then(() => {
          if (values.saveTemplate) onSavingTemplate();
          return onAfterSend();
        })
      }
    />
  );
};

type EmailClientPropsWithPeppol = Omit<
  EmailClientProps,
  'onSubmit' | 'onTest'
> & {
  onAfterSend?: () => any;
  context: EmailClientProps['context'] & {
    clientId: string;
    revenueId: string;
    clientType: IClient['type'];
  };
};

const emptySchema = Joi.any();

const peppolEvents = getTracingEvents({
  onTogglePeppol: {
    name: amplEvents.WEBAPP_SENDINVOICE_TOGGLEDPEPPOL,
    data: (value: boolean) => ({ value }),
  },
  onToggleEmail: {
    name: amplEvents.WEBAPP_SENDINVOICE_TOGGLEDEMAIL,
    data: (value: boolean) => ({ value }),
  },
  onSubmit: {
    name: amplEvents.WEBAPP_SENDEMAIL_CLICKEDSUBMITEMAIL,
    data: (data: any) => data,
  },
});

const RevenueEmailClientWithPeppol: FC<
  EmailClientPropsWithPeppol & {
    clientPeppolIntegration: ClientPeppolStatus | null;
    userPeppolIntegration: PeppolCurrentIntegration | null;
  }
> = (props) => {
  const { t } = useTranslation();
  const { data: user, refetch: refetchUser } = useGetCurrentCustomerData();
  const userOrExpertEmail = useGetLoggedInUserData().data?.email as string;

  const intials = useRevenueEmailIntialValues({
    context: props.context,
    defaultValues: props.defaultValues,
  });

  const canSendViaPeppol =
    props.userPeppolIntegration &&
    props.clientPeppolIntegration &&
    props.clientPeppolIntegration.exists &&
    props.clientPeppolIntegration.isReceivingOptionEnabled &&
    props.clientPeppolIntegration.isSupportedCountry &&
    props.userPeppolIntegration.status === IntegrationsStatusEnum.active;

  const formAPI = useForm({
    defaultValues: intials,
    resolver: (values, context, options) => {
      if (values.sendViaEmail) {
        const allAttachments = combineAttachments(
          values.externalAttachmentsObject,
          values.attachments,
        );
        return joiResolver(revenueEmailValidationSchema, {
          abortEarly: false,
          allowUnknown: true,
        })({ ...values, allAttachments }, context, options);
      }
      return joiResolver(emptySchema, {
        abortEarly: false,
        allowUnknown: true,
      })(values, context, options);
    },
  });

  const isCreditNote = props.context?.isCreditNote;

  console.log(formAPI.formState.errors);
  //
  const { isSubmitting } = formAPI.formState;

  const [sendViaPeppol, sendViaEmail] = formAPI.watch([
    'sendViaPeppol',
    'sendViaEmail',
  ]);

  const noSendOptionSelected = !sendViaPeppol && !sendViaEmail;

  const [hasEmailError, setHasEmailError] = useState(false);
  const [hasPeppolError, setHasPeppolError] = useState(false);

  const displayErrors = (data: BulkEmailErrorResponse) => {
    if (!data.error) {
      if (!data.reason) {
        return showErrorToast('Unknown error');
      }
      return showErrorToast(data.reason);
    }
    data.error.forEach((err) => {
      showErrorToast(t(`backend.errors.${err.code}`));
    });
  };

  const onAfterSend = () => {
    return Promise.resolve().then(props.onAfterSend).catch(console.error);
  };

  const sendWithPeppolOnly = () =>
    sendInvoiceEmailAndPeppol(props.context.revenueId, [
      { method: BulkEmailsMethods.PEPPOL },
    ]).then((result) => {
      const error = result.ops.find((op) => op.ok === false);
      if (error) {
        displayErrors(error as BulkEmailErrorResponse);
        throw new SentryIgnoredError('Failed to send via Peppol');
      }
      return onAfterSend();
    });

  const retryViaPeppolMutation = useMutation(sendWithPeppolOnly, {
    onSuccess: () => {
      setHasPeppolError(false);
    },
  });

  const sendWithEmailOnly = (data: EmailData) =>
    sendInvoiceEmailAndPeppol(props.context.revenueId, [
      { method: BulkEmailsMethods.EMAIL, data },
    ]).then((result) => {
      const error = result.ops.find((op) => op.ok === false);
      if (error) {
        displayErrors(error as BulkEmailErrorResponse);
        throw new SentryIgnoredError('Failed to send via Email');
      }
      if (data.saveTemplate) onSavingTemplate();
      return onAfterSend();
    });

  const retryViaEmailMutation = useMutation(sendWithEmailOnly, {
    onSuccess: () => {
      setHasEmailError(false);
    },
  });

  const onSavingTemplate = () => {
    refetchUser();
    showToast(
      {
        title: t('email_client.toast.template_saved'),
      },
      { type: 'success' },
    );
  };

  const resetAllMutations = () => {
    setHasEmailError(false);
    setHasPeppolError(false);
    retryViaEmailMutation.reset();
    retryViaPeppolMutation.reset();
  };

  const externalAttachmentsObject = formAPI.watch('externalAttachmentsObject');
  const isUploadingExternalAttachments = Object.values(
    externalAttachmentsObject,
  ).some((fileState) => fileState.isLoading);

  const globalLoading =
    isSubmitting ||
    retryViaPeppolMutation.isLoading ||
    retryViaEmailMutation.isLoading ||
    isUploadingExternalAttachments;

  return (
    <FormProvider {...formAPI}>
      <LayoutWithTitle isCreditNote={isCreditNote}>
        {noSendOptionSelected && (
          <p className="leading-tight text-primary-700 text-lg mt-2">
            {t('email_client.with_peppol.select_option')}
          </p>
        )}
        <FieldConnector
          name="sendViaPeppol"
          className="mt-6"
          disabled={globalLoading}
        >
          {({ field: { onChange, value, disabled } }) => (
            <div className="flex items-center gap-2">
              <Toggle
                disabled={disabled}
                enabled={!!value}
                onChange={(bool) => {
                  onChange(bool);
                  peppolEvents.onTogglePeppol(bool);
                }}
              />
              <label className="text-sm text-primary-700 flex items-center gap-1">
                {t('email_client.send_via_peppol.' + user?.country)}
                <PeppolHelper />
              </label>
            </div>
          )}
        </FieldConnector>
        {sendViaPeppol && (
          <>
            <PeppolUserSendingWarnings
              userPeppolIntegration={props.userPeppolIntegration}
              className="mt-3"
            />
            <PeppolClientSendingWarnings
              clientPeppolIntegration={props.clientPeppolIntegration}
              clientName={props.context?.clientName}
              className="mt-3"
            />
          </>
        )}
        <FieldConnector
          name="sendViaEmail"
          disabled={globalLoading}
          className="mt-6"
        >
          {({ field: { onChange, value, disabled } }) => (
            <div className="flex items-center gap-2">
              <Toggle
                disabled={disabled}
                enabled={!!value}
                onChange={(bool) => {
                  onChange(bool);
                  peppolEvents.onToggleEmail(bool);
                }}
              />
              <label className="text-sm text-primary-700">
                {t('email_client.send_via_email')}
              </label>
            </div>
          )}
        </FieldConnector>
        {sendViaEmail && (
          <div className="mt-4">
            <RevenueEmailClientControlled {...props} />
          </div>
        )}
        <div className="flex items-center justify-end gap-4 mt-4">
          {sendViaEmail && (
            <SendMeTestEmail
              disabled={globalLoading}
              tracingEvent={{
                name: amplEvents.WEBAPP_SENDEMAIL_CLICKEDSENDMETESTEMAIL,
                data: props.tracingData,
              }}
              onClick={() => {
                return formAPI
                  .trigger(['subject', 'htmlTemplate', 'attachments'])
                  .then((isValid) => {
                    if (!isValid) return Promise.reject();
                    const {
                      subject,
                      htmlTemplate,
                      attachments,
                      externalAttachmentsObject,
                    } = formAPI.getValues();
                    const data = {
                      to: [userOrExpertEmail],
                      subject,
                      htmlTemplate,
                      saveTemplate: false,
                      enableTracking: false,
                      ...adaptAttachmentsArray(attachments),
                      externalAttachments: externalAttachmentsObjectToArray(
                        externalAttachmentsObject,
                      ),
                    };
                    return sendRevenueEmail(props.context.revenueId, data);
                  });
              }}
            />
          )}
          <Button
            disabled={
              globalLoading ||
              noSendOptionSelected ||
              (sendViaPeppol && !canSendViaPeppol)
            }
            onClick={() => {
              peppolEvents.onSubmit({
                ...props.tracingData,
                method: [sendViaPeppol && 'peppol', sendViaEmail && 'email']
                  .filter(Boolean)
                  .join(' and '),
              });
              resetAllMutations();
              formAPI.handleSubmit((values) => {
                const data = {
                  ...values,
                  // add all attacments options as false, the override what we need to allow,
                  // keep it that way it can be useful in case of no attachments are required
                  // so we can diffrentiate between the empty states for example is it empty by us or by the user ( {} vs {attachment1: false} )
                  ...Object.values(AttachmentsEnum).reduce(
                    (acc, key) => ({ ...acc, [key]: false }),
                    {},
                  ),
                  // override the wanted attachments
                  ...adaptAttachmentsArray(values.attachments),
                  attachments: undefined,
                  externalAttachmentsObject: undefined,
                  allAttachments: undefined,
                  externalAttachments: externalAttachmentsObjectToArray(
                    values.externalAttachmentsObject,
                  ),
                };

                if (sendViaPeppol && sendViaEmail) {
                  return sendInvoiceEmailAndPeppol(props.context.revenueId, [
                    { method: BulkEmailsMethods.PEPPOL },
                    { method: BulkEmailsMethods.EMAIL, data },
                  ]).then((results) => {
                    let didPeppolFail = false;
                    let didEmailFail = false;
                    results.ops.forEach((result) => {
                      if (result.ok !== false) return;
                      displayErrors(result as BulkEmailErrorResponse);
                      if (result.method === BulkEmailsMethods.PEPPOL) {
                        didPeppolFail = true;
                      }
                      if (result.method === BulkEmailsMethods.EMAIL) {
                        didEmailFail = true;
                      }
                    });
                    if (didPeppolFail && didEmailFail) {
                      throw new SentryIgnoredError(
                        'Failed to send via Peppol and Email',
                      );
                    }

                    if (didEmailFail) {
                      setHasEmailError(true);
                      throw new SentryIgnoredError('Failed to send via Email');
                    }
                    // here we know email was sent successfully
                    if (data.saveTemplate) onSavingTemplate();

                    if (didPeppolFail) {
                      setHasPeppolError(true);
                      throw new SentryIgnoredError('Failed to send via Peppol');
                    }

                    tempTogglesApi.saveSendViaEmailOption(true);
                    tempTogglesApi.saveSendViaPeppolOption(true);

                    return onAfterSend();
                  });
                }

                if (sendViaPeppol) {
                  tempTogglesApi.saveSendViaEmailOption(false);
                  tempTogglesApi.saveSendViaPeppolOption(true);
                  return sendWithPeppolOnly();
                }

                tempTogglesApi.saveSendViaEmailOption(true);
                tempTogglesApi.saveSendViaPeppolOption(false);

                return sendWithEmailOnly(data);
              })();
            }}
          >
            <span className="flex items-center gap-2">
              {isSubmitting && <Loader size="lg" />}
              {props.okButtonText ??
                (isCreditNote
                  ? t('email_client.credit_note.submit.' + user?.country)
                  : t('email_client.invoice.submit'))}
            </span>
          </Button>
        </div>
        {!isSubmitting && hasEmailError && sendViaEmail && (
          <div className="mt-4">
            <Alert
              type="warning"
              size="sm"
              title={
                <div className="flex flex-wrap justify-between gap-4">
                  <span>
                    {t(
                      'email_client.with_peppol.send_via_email.failed.' +
                        user?.country,
                    )}
                  </span>
                  <Button
                    asDefault
                    className={
                      'text-[#F59E0B] text-sm font-semibold flex items-center gap-1'
                    }
                    disabled={
                      retryViaEmailMutation.isLoading ||
                      isUploadingExternalAttachments
                    }
                    onClick={() => {
                      formAPI.trigger().then((isValid) => {
                        if (!isValid) return;
                        retryViaEmailMutation.mutate(formAPI.getValues());
                      });
                    }}
                  >
                    {retryViaEmailMutation.isLoading && <Loader size="sm" />}
                    <span>{t('retry')}</span>
                  </Button>
                </div>
              }
            />
          </div>
        )}
        {!isSubmitting && hasPeppolError && sendViaPeppol && (
          <div className="mt-4">
            <Alert
              type="warning"
              size="sm"
              title={
                <div className="flex flex-wrap justify-between gap-4">
                  <span>
                    {t(
                      'email_client.with_peppol.send_via_peppol.failed.' +
                        user?.country,
                    )}
                  </span>

                  <Button
                    asDefault
                    className={
                      'text-[#F59E0B] text-sm font-semibold flex items-center gap-1'
                    }
                    disabled={
                      retryViaPeppolMutation.isLoading ||
                      isUploadingExternalAttachments
                    }
                    onClick={() => {
                      retryViaPeppolMutation.mutate();
                    }}
                  >
                    {retryViaPeppolMutation.isLoading && <Loader size="sm" />}
                    <span>{t('retry')}</span>
                  </Button>
                </div>
              }
            />
          </div>
        )}
      </LayoutWithTitle>
    </FormProvider>
  );
};

const RevenueEmailClientWithPeppolEntry: FC<EmailClientPropsWithPeppol> = (
  props,
) => {
  const user = useGetCurrentCustomerData().data as IUser;
  const canSeeToggleIfNotRegistered = useFeatures(
    'canSeePeppolEmailToggleIfNotRegistered',
  )(user.VATType);

  const isCreditNote = props.context?.isCreditNote;

  const userQuery = useUserPeppolIntegration();
  const clientQuery = useCheckClientPeppolStatus(
    props.context.clientId,
    isCreditNote ? 'creditnote' : 'invoice',
    {
      cacheTime: 0,
    },
  );

  const isLoading = userQuery.isLoading || clientQuery.isLoading;
  const isError = userQuery.isError || clientQuery.isError;

  if (isLoading) {
    return (
      <LayoutWithTitle isCreditNote={isCreditNote}>
        <CustomLoader className="min-h-[10rem]" />
      </LayoutWithTitle>
    );
  }

  if (isError) {
    return (
      <LayoutWithTitle isCreditNote={isCreditNote}>
        <SimpleError
          className="min-h-[10rem]"
          messageClassName="!text-xl"
          onRetry={() => {
            if (userQuery.isError) {
              userQuery.refetch();
            }
            if (clientQuery.isError) {
              clientQuery.refetch();
            }
          }}
        />
      </LayoutWithTitle>
    );
  }

  const isUserOnPeppol = !!userQuery.data;

  if (!isUserOnPeppol && !canSeeToggleIfNotRegistered) {
    return <SendInvoiceClient {...props} />;
  }

  return (
    <RevenueEmailClientWithPeppol
      {...props}
      clientPeppolIntegration={clientQuery.data || null}
      userPeppolIntegration={userQuery.data || null}
    />
  );
};

export const MaybeRevenueEmailClientWithPeppol: FC<
  EmailClientPropsWithPeppol
> = (props) => {
  if (props.context.clientType === ClientTypeEnum.private) {
    return <SendInvoiceClient {...props} />;
  }
  return <RevenueEmailClientWithPeppolEntry {...props} />;
};
